import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection, getDocs, doc, updateDoc,
  setDoc, deleteDoc, serverTimestamp, writeBatch
} from "firebase/firestore";
import Modal from "../../components/Modal";
import UISwitch from "../../components/UISwitch";
import { useLanguage } from "../../context/LanguageContext";
import {
  UserCheck,
} from "lucide-react";

function SupervisorsManager({ companyId }) {
  const { t } = useLanguage();
  const [supervisors, setSupervisors] = useState([]);
  const [vendors, setVendors]         = useState([]);
  const [sections, setSections]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [success, setSuccess]         = useState("");
  const [saving, setSaving]           = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ── Modal state ────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen]   = useState(false);
  const [editingSup, setEditingSup] = useState(null); // null = create mode

  // ── Delete state ───────────────────────────────────────────────────────────
  const [deleteModal, setDeleteModal]         = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [deleting, setDeleting]               = useState(false);

  // ── Multi-select ───────────────────────────────────────────────────────────
  const [selected, setSelected]     = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  const emptyForm = {
    firstName: "", lastName: "", email: "",
    password: "", phone: "",
    sections: [],  // assigned zone IDs
    vendors:  [],  // assigned vendor IDs
    notes: "",
    active: true,
  };
  const [form, setForm] = useState(emptyForm);

  // ── Vendor search inside modal ─────────────────────────────────────────────
  const [vendorSearch, setVendorSearch] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [usersSnap, sectionsSnap] = await Promise.all([
        getDocs(collection(db, "companies", companyId, "users")),
        getDocs(collection(db, "companies", companyId, "sections")),
      ]);
      const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSupervisors(allUsers.filter(u => u.role === "supervisor"));
      setVendors(allUsers.filter(u => u.role === "vendor"));
      setSections(sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError("Failed to load: " + err.message);
    }
    setLoading(false);
  }

  function flash(msg, isError = false) {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(""); setSuccess(""); }, 4000);
  }

  // ── Open modals ────────────────────────────────────────────────────────────
  function openCreate() {
    setEditingSup(null);
    setForm(emptyForm);
    setVendorSearch("");
    setShowPassword(false);
    setModalOpen(true);
  }

  function openEdit(sup) {
    setEditingSup(sup);
    setForm({
      firstName: sup.first_name || "",
      lastName:  sup.last_name  || "",
      email:     sup.email      || "",
      password:  "",
      phone:     sup.phone      || "",
      sections:  sup.sections   || [],
      vendors:   sup.vendors    || [],
      notes:     sup.notes      || "",
      active:    sup.active !== false,
    });
    setVendorSearch("");
    setShowPassword(false);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingSup(null);
    setForm(emptyForm);
    setError("");
    setVendorSearch("");
  }

  // ── Toggle section/vendor in form ──────────────────────────────────────────
  function toggleSection(id) {
    setForm(p => ({
      ...p,
      sections: p.sections.includes(id)
        ? p.sections.filter(s => s !== id)
        : [...p.sections, id],
    }));
  }

  function toggleVendor(id) {
    setForm(p => ({
      ...p,
      vendors: p.vendors.includes(id)
        ? p.vendors.filter(v => v !== id)
        : [...p.vendors, id],
    }));
  }

  // ── Save supervisor ────────────────────────────────────────────────────────
  async function handleSave(e) {
    e.preventDefault();
    setError("");
    if (!form.firstName.trim()) return setError("First name is required.");
    if (!form.email.trim())     return setError("Email is required.");
    if (!editingSup && form.password.length < 6)
      return setError("Password must be at least 6 characters.");

    setSaving(true);
    try {
      if (editingSup) {
        // ── Edit existing supervisor ──
        const data = {
          first_name: form.firstName.trim(),
          last_name:  form.lastName.trim(),
          name:       `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
          email:      form.email.trim(),
          phone:      form.phone.trim(),
          sections:   form.sections,
          vendors:    form.vendors,
          notes:      form.notes.trim(),
          active:     form.active,
          updated_at: serverTimestamp(),
        };
        // Only update password if provided
        if (form.password.trim()) data.password = form.password.trim();

        await updateDoc(doc(db, "companies", companyId, "users", editingSup.id), data);
        setSupervisors(prev => prev.map(s =>
          s.id === editingSup.id ? { ...s, ...data, password: undefined } : s
        ));
        flash("✅ Supervisor updated!");

      } else {
        // ── Create new supervisor via Firestore trigger ──
        const supId = "supervisor-" + Date.now().toString(36);
        const data = {
          first_name: form.firstName.trim(),
          last_name:  form.lastName.trim(),
          name:       `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
          email:      form.email.trim(),
          password:   form.password.trim(), // Cloud Function reads and deletes this
          phone:      form.phone.trim(),
          role:       "supervisor",
          sections:   form.sections,
          vendors:    form.vendors,
          notes:      form.notes.trim(),
          active:     form.active,
          companyId:  companyId,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        };

        await setDoc(doc(db, "companies", companyId, "users", supId), data);
        setSupervisors(prev => [...prev, { id: supId, ...data, password: undefined }]);
        flash(`✅ Supervisor "${form.firstName}" created! Login account being set up...`);
      }

      closeModal();
    } catch (err) {
      setError("Error: " + err.message);
    }
    setSaving(false);
  }

  // ── Toggle active ──────────────────────────────────────────────────────────
  async function toggleActive(supId, current) {
    try {
      await updateDoc(doc(db, "companies", companyId, "users", supId), {
        active: !current, updated_at: serverTimestamp(),
      });
      setSupervisors(prev => prev.map(s =>
        s.id === supId ? { ...s, active: !current } : s
      ));
    } catch (err) { flash("Error: " + err.message, true); }
  }

  // ── Single delete ──────────────────────────────────────────────────────────
  function askDelete(sup) { setDeleteTarget(sup); setDeleteModal(true); }

  async function confirmSingleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "companies", companyId, "users", deleteTarget.id));
      setSupervisors(prev => prev.filter(s => s.id !== deleteTarget.id));
      setSelected(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      setDeleteModal(false);
      setDeleteTarget(null);
      flash("✅ Supervisor deleted.");
    } catch (err) { flash("Error: " + err.message, true); }
    setDeleting(false);
  }

  // ── Bulk delete ────────────────────────────────────────────────────────────
  async function confirmBulkDelete() {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const ids    = Array.from(selected);
      const chunks = [];
      for (let i = 0; i < ids.length; i += 500) chunks.push(ids.slice(i, i + 500));
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(id => batch.delete(doc(db, "companies", companyId, "users", id)));
        await batch.commit();
      }
      setSupervisors(prev => prev.filter(s => !selected.has(s.id)));
      flash(`✅ Deleted ${selected.size} supervisor(s).`);
      setSelected(new Set());
      setSelectMode(false);
      setBulkDeleteModal(false);
    } catch (err) { flash("Error: " + err.message, true); }
    setDeleting(false);
  }

  // ── Selection helpers ──────────────────────────────────────────────────────
  function toggleSelect(id) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleSelectAll() {
    setSelected(
      selected.size === supervisors.length
        ? new Set()
        : new Set(supervisors.map(s => s.id))
    );
  }

  function exitSelectMode() { setSelectMode(false); setSelected(new Set()); }

  // ── Helpers ────────────────────────────────────────────────────────────────
  function getSectionName(id) {
    return sections.find(s => s.id === id)?.name || id;
  }

  function getVendorName(id) {
    const v = vendors.find(v => v.id === id);
    return v ? `${v.first_name} ${v.last_name}` : id;
  }

  const filteredVendors = vendors.filter(v =>
    `${v.first_name} ${v.last_name} ${v.plate} ${v.email}`
      .toLowerCase().includes(vendorSearch.toLowerCase())
  );

  const allSelected = supervisors.length > 0 && supervisors.every(s => selected.has(s.id));

  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <p className="text-gray-500 text-sm">{t("sup_loading")}</p>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("sup_title")}</h2>
          <p className="text-gray-500 text-sm mt-1">
            {supervisors.length} {t("role_supervisor").toLowerCase()}{supervisors.length !== 1 ? "s" : ""}
            {supervisors.filter(s => s.active !== false).length > 0 && (
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                {supervisors.filter(s => s.active !== false).length} {t("sup_active")}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!selectMode ? (
            <button onClick={() => setSelectMode(true)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
              ☑️ {t("sup_select")}
            </button>
          ) : (
            <button onClick={exitSelectMode}
              className="bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
              {t("sup_cancel")}
            </button>
          )}
          <button onClick={openCreate}
            className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition">
            {t("sup_add")}
          </button>
        </div>
      </div>

      {/* ── Info box ────────────────────────────────────────────────────────── */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <p className="text-purple-700 text-sm">
          🔭 <strong>{t("sup_supervisor_access")}:</strong> {t("sup_access_hint")}
        </p>
      </div>

      {/* ── Bulk action bar ──────────────────────────────────────────────────── */}
      {selectMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
              className="w-4 h-4 accent-green-600 cursor-pointer" />
            <span className="text-sm text-blue-800 font-medium">
              {selected.size === 0
                ? t("sup_select_for_bulk")
                : `${selected.size} ${t("sup_selected_count")}`}
            </span>
          </div>
          {selected.size > 0 && (
            <button onClick={() => setBulkDeleteModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
              🗑 {t("delete")} ({selected.size})
            </button>
          )}
        </div>
      )}

      {/* ── Feedback ────────────────────────────────────────────────────────── */}
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      {/* ── Empty state ──────────────────────────────────────────────────────── */}
      {supervisors.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <UserCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">{t("sup_no_supervisors")}</p>
          <p className="text-gray-400 text-sm mt-1">
            {t("sup_create_assign_hint")}
          </p>
          <button onClick={openCreate}
            className="mt-4 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
            {t("sup_add")}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {supervisors.map(sup => {
            const isSelected   = selected.has(sup.id);
            const zoneNames    = (sup.sections || []).map(id => getSectionName(id));
            const vendorNames  = (sup.vendors  || []).map(id => getVendorName(id));
            return (
              <div key={sup.id}
                className={`bg-white rounded-xl shadow-sm p-5 transition ${
                  isSelected ? "ring-2 ring-blue-400 bg-blue-50" : ""
                }`}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex items-start gap-4">

                    {/* Checkbox */}
                    {selectMode && (
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(sup.id)}
                        className="w-4 h-4 accent-blue-600 cursor-pointer mt-1 flex-shrink-0" />
                    )}

                    {/* Avatar */}
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 ${
                      sup.active !== false ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-400"
                    }`}>
                      {(sup.first_name || "?").charAt(0).toUpperCase()}
                    </div>

                    <div className="space-y-1.5">
                      {/* Name + status */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`font-medium ${sup.active !== false ? "text-gray-800" : "text-gray-400"}`}>
                          {sup.first_name} {sup.last_name}
                        </p>
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                          {t("sup_supervisor_badge")}
                        </span>
                        {sup.active === false && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                            {t("disabled")}
                          </span>
                        )}
                      </div>

                      {/* Contact */}
                      <div className="flex flex-wrap gap-3">
                        {sup.email && <p className="text-gray-400 text-xs">✉️ {sup.email}</p>}
                        {sup.phone && <p className="text-gray-400 text-xs">📞 {sup.phone}</p>}
                      </div>

                      {/* Assigned zones */}
                      {zoneNames.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs text-gray-400 mr-1">{t("sup_zones_label")}</span>
                          {zoneNames.map((name, i) => (
                            <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                              🗂 {name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-orange-500">⚠️ {t("sup_no_zones")}</p>
                      )}

                      {/* Assigned vendors */}
                      {vendorNames.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs text-gray-400 mr-1">{t("sup_vendors_label")}</span>
                          {vendorNames.map((name, i) => (
                            <span key={i} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">
                              🚗 {name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-orange-500">⚠️ {t("sup_no_vendors")}</p>
                      )}

                      {/* Notes */}
                      {sup.notes && (
                        <p className="text-xs text-gray-400 italic">📝 {sup.notes}</p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap justify-end sm:justify-start sm:flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {sup.active !== false ? t("active") : t("disabled")}
                      </span>
                      <UISwitch
                        value={sup.active !== false}
                        onChange={() => toggleActive(sup.id, sup.active !== false)}
                        size="sm"
                      />
                    </div>
                    <button onClick={() => openEdit(sup)}
                      className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-medium">
                      {t("edit")}
                    </button>
                    <button onClick={() => askDelete(sup)}
                      className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium">
                      {t("delete")}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Create / Edit Modal ────────────────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingSup
          ? `${t("edit")} — ${editingSup.first_name} ${editingSup.last_name}`
          : t("sup_new_supervisor")}
        size="lg"
      >
        <form onSubmit={handleSave} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
          )}

          {!editingSup && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-700 text-sm">
                🔐 {t("sup_login_auto")}
              </p>
            </div>
          )}

          {/* ── Basic info ── */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t("sup_basic_info")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_first_name")} *</label>
                <input value={form.firstName} onChange={e => setForm(p => ({...p, firstName: e.target.value}))} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t("form_first_name")} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_last_name")}</label>
                <input value={form.lastName} onChange={e => setForm(p => ({...p, lastName: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t("form_last_name")} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("form_email")} * <span className="text-gray-400 text-xs font-normal">{t("form_email_login")}</span>
                </label>
                <input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="supervisor@company.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_phone")}</label>
                <input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="+971 50 000 0000" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("form_password")} {editingSup
                    ? <span className="text-gray-400 text-xs font-normal">{t("form_password_keep")}</span>
                    : "*"}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={e => setForm(p => ({...p, password: e.target.value}))}
                    required={!editingSup}
                    minLength={editingSup ? 0 : 6}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 pr-16"
                    placeholder={editingSup ? t("form_password_keep") : t("form_password_min")}
                  />
                  <button type="button" onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-medium">
                    {showPassword ? t("form_hide") : t("form_show")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Assign Zones ── */}
          <div className="border-t pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              {t("sup_assigned_zones_label")}
              <span className="ml-2 text-gray-400 font-normal normal-case">
                {t("sup_zones_hint")}
              </span>
            </p>
            {sections.length === 0 ? (
              <p className="text-sm text-gray-400 italic">{t("sup_no_zones_defined")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {sections.map(section => {
                  const sel = form.sections.includes(section.id);
                  return (
                    <button key={section.id} type="button" onClick={() => toggleSection(section.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition flex items-center gap-1.5 ${
                        sel
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                      }`}>
                      {sel && "✓ "}
                      🗂 {section.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Assign Vendors ── */}
          <div className="border-t pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {t("sup_assigned_vendors_label")}
              <span className="ml-2 text-gray-400 font-normal normal-case">
                {t("sup_vendors_hint")}
              </span>
            </p>

            {vendors.length === 0 ? (
              <p className="text-sm text-gray-400 italic mt-2">{t("sup_no_vendors_exist")}</p>
            ) : (
              <>
                {/* Vendor search */}
                <input
                  value={vendorSearch}
                  onChange={e => setVendorSearch(e.target.value)}
                  placeholder={t("sup_search_vendors")}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-2 mb-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />

                {/* Selected count */}
                {form.vendors.length > 0 && (
                  <p className="text-xs text-green-700 font-medium mb-2">
                    ✓ {form.vendors.length} vendor{form.vendors.length > 1 ? "s" : ""} selected
                  </p>
                )}

                {/* Vendor list */}
                <div className="border border-gray-200 rounded-xl overflow-hidden max-h-56 overflow-y-auto">
                  {filteredVendors.length === 0 ? (
                    <p className="text-sm text-gray-400 p-4 text-center">{t("vendors_no_vendors")}</p>
                  ) : (
                    filteredVendors.map(vendor => {
                      const sel = form.vendors.includes(vendor.id);
                      return (
                        <div key={vendor.id}
                          onClick={() => toggleVendor(vendor.id)}
                          className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition border-b border-gray-100 last:border-0 ${
                            sel ? "bg-green-50" : "hover:bg-gray-50"
                          }`}>
                          <input type="checkbox" checked={sel} readOnly
                            className="w-4 h-4 accent-green-600 pointer-events-none flex-shrink-0" />
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                            vendor.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                          }`}>
                            {(vendor.first_name || "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {vendor.first_name} {vendor.last_name}
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                              {vendor.plate && `🚗 ${vendor.plate}`}
                              {vendor.plate && vendor.email && " · "}
                              {vendor.email}
                            </p>
                          </div>
                          {vendor.active === false && (
                            <span className="text-xs bg-red-50 text-red-500 px-1.5 py-0.5 rounded flex-shrink-0">
                              {t("inactive")}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>

          {/* ── Notes + Active ── */}
          <div className="border-t pt-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("sup_notes")}</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={t("sup_notes_ph")} />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">{t("sup_status_label")}</label>
              <UISwitch value={form.active} onChange={val => setForm(p => ({...p, active: val}))} />
              <span className="text-sm text-gray-500">{form.active ? t("active") : t("disabled")}</span>
            </div>
          </div>

          {/* ── Buttons ── */}
          <div className="flex gap-3 pt-2 border-t">
            <button type="submit" disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-5 py-2 rounded-lg transition disabled:opacity-50">
              {saving ? t("saving") : editingSup ? t("save_changes") : t("sup_create")}
            </button>
            <button type="button" onClick={closeModal}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg transition">
              {t("cancel")}
            </button>
          </div>
        </form>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Single Delete Modal ────────────────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={deleteModal}
        onClose={() => { setDeleteModal(false); setDeleteTarget(null); }}
        title={t("sup_delete_title")}
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-red-600 text-lg">🗑</span>
            </div>
            <div>
              <p className="text-gray-800 font-medium">
                Delete "{deleteTarget?.first_name} {deleteTarget?.last_name}"?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {t("sup_delete_msg")}
              </p>
            </div>
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button onClick={confirmSingleDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
              {deleting ? t("deleting") : `🗑 ${t("yes_delete")}`}
            </button>
            <button onClick={() => { setDeleteModal(false); setDeleteTarget(null); }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
              {t("cancel")}
            </button>
          </div>
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Bulk Delete Modal ──────────────────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        title={t("sup_bulk_delete")}
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-lg">⚠️</span>
            </div>
            <div>
              <p className="text-gray-800 font-medium">
                Delete {selected.size} supervisor{selected.size > 1 ? "s" : ""}?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {t("sup_delete_msg")}
              </p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
            {Array.from(selected).map(id => {
              const s = supervisors.find(x => x.id === id);
              if (!s) return null;
              return (
                <p key={id} className="text-xs text-gray-600">
                  • {s.first_name} {s.last_name} — {s.email}
                </p>
              );
            })}
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button onClick={confirmBulkDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
              {deleting ? t("deleting") : `🗑 ${t("delete_all")} (${selected.size})`}
            </button>
            <button onClick={() => setBulkDeleteModal(false)}
              className="bg-gray-100 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
              {t("cancel")}
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}

export default SupervisorsManager;
